import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Card, Form } from "react-bootstrap";

import UserSideBarMenu from "../Component/UserSidebarMenu";
import TopHeader from "../Component/TopHeader";
import ConfirmModal from "../Component/Models/ConfirmModal";

import { showToast } from "../../utils/helper";
import { trackEvent } from "../../utils/analytics";
import {
  deleteAccountAction,
  updateProfileAction,
} from "../../Store/Actions/AuthActions";

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const menuState = useSelector((state) => state.auth.isMenuOpen);
  const { user } = useSelector((state) => state.auth);

  const [data, setData] = useState({ name: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Hydrate the form once the user is available in the store.
  useEffect(() => {
    setData({
      name: user.name || "",
      phone: user.phone || "",
    });
  }, [user.name, user.phone]);

  useEffect(() => {
    setImageFailed(false);
  }, [user.image]);

  const onChange = (e) =>
    setData((d) => ({ ...d, [e.target.name]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!data.name || data.name.trim() === "") {
      errs.name = "Please enter your name";
    }
    return errs;
  };

  const submitForm = () => {
    const errs = validate();
    setErrors(errs);
    if (saving || Object.keys(errs).length > 0) return;

    setSaving(true);
    updateProfileAction(
      { id: user.id, name: data.name, phone: data.phone },
      dispatch,
    )
      .then((res) => {
        if (res && res.type === "success") {
          showToast("success", res.message || "Profile updated");
        } else {
          showToast("error", (res && res.message) || "Could not update profile");
        }
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not update profile"),
      )
      .finally(() => setSaving(false));
  };

  const performDelete = () => {
    if (deleting) return;
    setDeleting(true);
    deleteAccountAction({ id: user.id }, dispatch)
      .then((res) => {
        if (res && res.type === "success") {
          trackEvent("web_account_deleted");
          showToast("success", res.message || "Account deleted");
          setDeleteModal(false);
          navigate("/");
        } else {
          showToast("error", (res && res.message) || "Could not delete account");
        }
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not delete account"),
      )
      .finally(() => setDeleting(false));
  };

  const initials = getInitials(user.name);
  const showImage = user.image && !imageFailed;

  return (
    <div className={`app-wrapper ${menuState ? "open" : ""}`}>
      <UserSideBarMenu />

      <div className="content-wrapper">
        <TopHeader title={"Profile"} />

        <div className="content">
          <Card className="profile-card">
            <Card.Body>
              <div className="profile-head">
                <div className="profile-avatar">
                  {showImage ? (
                    <img
                      src={user.image}
                      alt={user.name}
                      onError={() => setImageFailed(true)}
                    />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
                <div className="profile-head-meta">
                  <h3>{user.name || "—"}</h3>
                  <p>{user.email || ""}</p>
                </div>
              </div>

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitForm();
                }}
              >
                <Form.Group className="form-group with-icon">
                  <i className="far fa-user" />
                  <Form.Control
                    type="text"
                    name="name"
                    value={data.name}
                    placeholder="Your name"
                    onChange={onChange}
                    isInvalid={!!errors.name}
                  />
                  {errors.name && (
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="form-group with-icon">
                  <i className="far fa-envelope" />
                  <Form.Control
                    type="email"
                    name="email"
                    value={user.email || ""}
                    placeholder="Your email"
                    disabled
                  />
                </Form.Group>

                <Form.Group className="form-group with-icon">
                  <i className="fas fa-phone" />
                  <Form.Control
                    type="text"
                    name="phone"
                    value={data.phone}
                    placeholder="Your phone"
                    onChange={onChange}
                  />
                </Form.Group>

                <div className="button-wrapper">
                  <Button
                    type="submit"
                    variant=""
                    className="btn-pill-primary"
                    disabled={saving}
                  >
                    <span>
                      {saving && (
                        <i className="fas fa-spin fa-spinner me-2" />
                      )}
                      Save Changes
                    </span>
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="profile-card danger-card">
            <Card.Body>
              <h4>Delete Account</h4>
              <p>
                Permanently delete your account and all associated data. This
                action cannot be undone.
              </p>
              <Button
                variant=""
                className="btn-danger-outline"
                onClick={() => setDeleteModal(true)}
              >
                <span>
                  <i className="fas fa-trash me-2" />
                  Delete Account
                </span>
              </Button>
            </Card.Body>
          </Card>
        </div>
      </div>

      <ConfirmModal
        show={deleteModal}
        title="Delete Account"
        message="Are you sure want to delete your account? This action is permanent and cannot be undone."
        confirmLabel="Yes, delete"
        cancelLabel="No"
        busy={deleting}
        onClose={() => setDeleteModal(false)}
        onConfirm={performDelete}
      />
    </div>
  );
};

export default Profile;
